import defaultConfig from '../../config/responseMappings/default.json';

// Any src/config/responseMappings/<moduleName>.json is auto-registered as a
// per-module override, shallow-merged over defaultConfig — no code change needed.
const overrideConfigs = import.meta.glob('../../config/responseMappings/*.json', { eager: true });

function getModuleOverride(moduleName) {
  if (!moduleName) return null;

  for (const path of Object.keys(overrideConfigs)) {
    const fileName = path.split('/').pop().replace(/\.json$/, '');
    if (fileName === moduleName) {
      const mod = overrideConfigs[path];
      return mod.default ?? mod;
    }
  }

  return null;
}

function loadMappingConfig(moduleName) {
  const override = getModuleOverride(moduleName);
  return override ? { ...defaultConfig, ...override } : defaultConfig;
}

function getPath(source, path) {
  return String(path).split('.').reduce((value, key) => (value == null ? undefined : value[key]), source);
}

function resolveFirst(response, payload, spec) {
  if (!spec) return undefined;
  const sourceMap = { response, payload };

  for (const sourceName of spec.sources) {
    for (const path of spec.paths) {
      const value = getPath(sourceMap[sourceName], path);
      if (value !== undefined) return value;
    }
  }

  return undefined;
}

function humanizeKey(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
}

// Substrings that flag a key as sensitive; synthesis must never surface these
// automatically even though they are primitive values. See
// docs/JSON-Driven-Response-Mapping-Security-Assessment.html — "Accidental
// sensitive-data exposure".
const SENSITIVE_KEY_PATTERN = /token|password|passwd|secret|apikey|api_key|auth|credential|salary|ssn|ein\b/i;

function isSensitiveKey(key) {
  return SENSITIVE_KEY_PATTERN.test(key);
}

export function synthesizeFieldsFromRows(rows) {
  const firstRow = rows[0];
  if (!firstRow) return [];

  return Object.keys(firstRow)
    .filter((key) => !key.startsWith('_') && !isSensitiveKey(key) && typeof firstRow[key] !== 'object')
    .map((key) => ({
      value: key,
      field: key,
      label: humanizeKey(key),
      isVisible: true,
    }));
}

export function applyVisibilityFlag(fields, flagKey) {
  if (!flagKey) return fields;

  return fields
    .filter((field) => field[flagKey] === 1 || field[flagKey] === true || field[flagKey] === undefined)
    .map((field) => ({ ...field, isVisible: true }));
}

// Collection caps: an untrusted response with huge arrays can freeze the
// browser (see "Unbounded response size" in the security assessment).
const MAX_ROWS = 5000;
const MAX_FIELDS = 300;
const MAX_TABS = 100;
const MAX_ACTIONS = 100;
const MAX_ACTION_RULES = 300;
const MAX_COLUMN_ACTIONS = 100;

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function clampArray(value, max) {
  return Array.isArray(value) ? value.slice(0, max) : [];
}

// Backend metadata is trusted with minimal validation ("Backend metadata is
// trusted" finding). A field/tab/action array must at least be an array of
// plain objects before it reaches ListView.
function sanitizeObjectList(value, max) {
  return clampArray(value, max).filter(isPlainObject);
}

function sanitizeFieldList(value, max) {
  return sanitizeObjectList(value, max).filter(
    (field) => typeof field.value === 'string' || typeof field.field === 'string'
  );
}

// javascript:/data:/vbscript:/file: URLs in action metadata can reach a
// rendered <a href> (ListView's renderActionButton) and execute in the
// user's session. Only allow relative routes and standard web protocols.
const UNSAFE_URL_PROTOCOL = /^\s*(javascript|data|vbscript|file):/i;
const ACTION_URL_KEYS = ['url', 'href', 'routeUrl', 'routeURL'];

function sanitizeAction(action) {
  const safe = { ...action };
  for (const key of ACTION_URL_KEYS) {
    if (typeof safe[key] === 'string' && UNSAFE_URL_PROTOCOL.test(safe[key])) {
      delete safe[key];
    }
  }
  return safe;
}

function sanitizeActionList(value, max) {
  return sanitizeObjectList(value, max).map(sanitizeAction);
}

export function mapListResponse(response, moduleName) {
  const config = loadMappingConfig(moduleName);
  const payload = response?.data ?? response;

  const rowsRaw = Array.isArray(payload) ? payload : resolveFirst(response, payload, config.rows);
  const rows = clampArray(rowsRaw, MAX_ROWS);

  const totalRaw = resolveFirst(response, payload, config.total);
  const total = Number(totalRaw ?? rows.length) || 0;

  const tabs = sanitizeObjectList(resolveFirst(response, payload, config.tabs), MAX_TABS);

  const fieldsRaw = resolveFirst(response, payload, config.fields);
  let fields = Array.isArray(fieldsRaw) ? fieldsRaw : [];
  if (fields.length === 0 && config.synthesizeFieldsFromRows) {
    fields = synthesizeFieldsFromRows(rows);
  }
  fields = sanitizeFieldList(fields, MAX_FIELDS);
  if (config.visibilityFlag) {
    fields = applyVisibilityFlag(fields, config.visibilityFlag);
  }

  const tabFieldRaw = resolveFirst(response, payload, config.tabField);
  const tabField = typeof tabFieldRaw === 'string' ? tabFieldRaw : '';

  return {
    rows,
    total,
    tabs,
    fields,
    tabField,
    actionRules: sanitizeActionList(resolveFirst(response, payload, config.actionRules), MAX_ACTION_RULES),
    actions: sanitizeActionList(resolveFirst(response, payload, config.actions), MAX_ACTIONS),
    columnActions: sanitizeActionList(resolveFirst(response, payload, config.columnActions), MAX_COLUMN_ACTIONS),
  };
}
